import { inject, Injectable } from "@angular/core";
import { Firestore, query, getDocs, collection, where, Timestamp, doc, setDoc, getDoc, orderBy, updateDoc, arrayUnion } from "@angular/fire/firestore";
import { AssessmentQuestion } from "../models/assessment/assessment-question.model";
import { TenantService } from "./tenant.service";
import { AssessmentAttempt, LatestAssessmentAttempt } from "../models/assessment/assessment-attempt.model";
import { createFirestoreId } from "../common/firestore-utils";
import { AssessmentAnswer } from "../models/assessment/assessment-answer.model";
import { Lesson } from "../models/lesson.model";
import { MessagesService } from '../shared/services/messages.service';

@Injectable({
  providedIn: 'root'
})
export class AssessmentService {
  private firestore: Firestore = inject(Firestore);
  private tenant: TenantService = inject(TenantService);
  private messagesService: MessagesService = inject(MessagesService);


  constructor() { }

  /**
   * Get all assessment questions for a tenant, ordered by their order field
   */
  async getLessonAssessmentQuestions(
    tenantId: string,
    lessonId: string,
    courseId: string,
  ): Promise<AssessmentQuestion[]> {
    try {
      const lessonRef = doc(
        this.firestore,
        `schools/${tenantId}/courses/${courseId}/lessons/${lessonId}`
      );
      const lessonDoc = await getDoc(lessonRef);

      if (!lessonDoc.exists()) {
        return [];
      }

      const lessonData = lessonDoc.data() as Lesson;
      const questionIds: string[] = lessonData?.assessment?.assessmentQuestionIds || [];

      const questionsMap = new Map<string, AssessmentQuestion>();


        if (questionIds.length === 0) {
          return [];
        }

        const BATCH_SIZE = 10;
        const questionIdBatches: string[][] = [];

        for (let i = 0; i < questionIds.length; i += BATCH_SIZE) {
          questionIdBatches.push(questionIds.slice(i, i + BATCH_SIZE));
        }

        for (const batch of questionIdBatches) {
          const batchQuerySnapshot = await getDocs(
            query(
              collection(this.firestore, `schools/${tenantId}/assessmentQuestions`),
              where("id", "in", batch),
              where('isDeleted', '==', false),
            )
          );
          batchQuerySnapshot.forEach(doc => {
            questionsMap.set(doc.id, { id: doc.id, ...doc.data() } as AssessmentQuestion);
          });
        }


      const orderedQuestions = questionIds.map((id) => questionsMap.get(id)).filter((q) => !!q);

      return orderedQuestions as AssessmentQuestion[];

    } catch (error: any) {
      return [] as AssessmentQuestion[];
    }
  }
  /**
   * Creates a new assessment attempt.
   * @param userId The ID of the user attempting the assessment.
   * @param assessmentId The ID of the assessment.
   * @returns A Promise that resolves with the ID of the created attempt.
   */
  async startAssessmentAttempt(userId: string, assessmentId: string,totalQuestions: number, courseId: string, attemptNumber: number): Promise<string> {
    const attemptId = createFirestoreId(this.firestore);
    const startedAt = Timestamp.now();

    const newAttempt: AssessmentAttempt = {
      id: attemptId,
      userId: userId,
      assessmentId: assessmentId,
      courseId: courseId,
      startedAt: startedAt,
      submittedAt: null, // Initialize, will be updated on submission
      timeSpent: 0,
      totalQuestions: totalQuestions,
      score: 0,
      passed: null,
      status: 'started',
      attemptNumber: attemptNumber,
      answers: [],
      isManuallyGraded: false,
      gradedBy: null,
      gradingNotes: '',
      manualScore: null
    };

    // Reference to the assessment attempt document
    const attemptDocRef = doc(this.firestore, `schools/${this.tenant.id}/latestAssessmentAttempts/${attemptId}`);

    try {
      await setDoc(attemptDocRef, newAttempt);
      return attemptId;
    } catch (error) {
      throw error;
    }
  }

   /**
   * Submits an assessment attempt.
   * @param attemptId The ID of the attempt to submit.
   * @param answers The array of AssessmentAnswer objects.
   * @param timeSpent The total time spent on the assessment in seconds.
   * @param score The calculated score.
   * @param passed Whether the student passed.
   */
   async submitAssessmentAttempt(
    attemptId: string,
    answers: AssessmentAnswer[],
    timeSpent: number,
    status: string,
    minimumPassingGrade: number,
    totalQuestions: number,
    isManualGradingRequired?: boolean
  ): Promise<LatestAssessmentAttempt> {
    const attemptDocRef = doc(this.firestore, `schools/${this.tenant.id}/latestAssessmentAttempts/${attemptId}`);
    try {
      // Calculate score, passed status based on userAnswers (auto-graded)
     const score = (answers || []).filter(answer => answer.isCorrect).length;
      const passed = (score / totalQuestions) * 100 >= (minimumPassingGrade);

      if(passed) {
        status = 'completed';
      } else {
        status = 'failed';
      }

      await setDoc(attemptDocRef, {
        submittedAt: Timestamp.now(),
        gradedAt: isManualGradingRequired ? null : Timestamp.now(),
        timeSpent: timeSpent,
        status: isManualGradingRequired ? 'submitted' : status,
        answers: answers,
        score: isManualGradingRequired ? null : score,
        passed: isManualGradingRequired ? null : passed,
        totalQuestions: totalQuestions,
        // Fields for manual grading
        isManuallyGraded: isManualGradingRequired || false,
        manualScore: null,
        gradedBy: isManualGradingRequired ? null : 'auto',
        gradingNotes: ''
      }, { merge: true });

      return this.getAssessmentAttempt(attemptId);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Retrieves an assessment attempt by its ID.
   * @param attemptId The ID of the attempt.
   * @returns An Observable of the AssessmentAttempt, or null if not found.
   */
 async  getAssessmentAttempt(attemptId: string): Promise<LatestAssessmentAttempt> {
  try {
    const attemptDocRef = doc(this.firestore, `schools/${this.tenant.id}/latestAssessmentAttempts/${attemptId}`);
    const snapshot = await getDoc(attemptDocRef);
    if (snapshot.exists()) {
      return { id: snapshot.id, ...(snapshot.data() as LatestAssessmentAttempt) };
    }
  } catch (error) {
    throw error;
  }
  }

  /**
   * Fetches the latest assessment attempt for a specific user and assessment.
   * This is used to determine the attemptNumber and previousAttempts.
   * @param userId The user's ID.
   * @param assessmentId The assessment's ID.
   * @returns The latest attempt or null if none exist.
   */
 async getLatestAttempt(userId: string, assessmentId: string){
  const attemptsCollectionRef = collection(this.firestore, `schools/${this.tenant.id}/latestAssessmentAttempts`);

  const q = query(
    attemptsCollectionRef,
    where('userId', '==', userId),        // Filter by userId
    where('assessmentId', '==', assessmentId), // Filter by assessmentId
    orderBy('startedAt', 'desc')           // Order by startedAt descending
  );

  try {
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

      const latestDoc = querySnapshot.docs[0];

    // Return the single document data with its ID.
    return { id: latestDoc.id, ...(latestDoc.data() as LatestAssessmentAttempt) };

  } catch (error) {
    throw error; // Re-throw the error for the calling component to handle
  }
}


async getCourseAssessments(userId: string, courseId: string){
  const attemptsCollectionRef = collection(this.firestore, `schools/${this.tenant.id}/latestAssessmentAttempts`);

  const q = query(
    attemptsCollectionRef,
    where('userId', '==', userId),        // Filter by userId
    where('courseId', '==', courseId), // Filter by courseId
    orderBy('startedAt', 'desc')           // Order by startedAt descending
  );

  try {
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

      const attempts = querySnapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as LatestAssessmentAttempt) }));

    return attempts;

  } catch (error) {
    throw error; // Re-throw the error for the calling component to handle
  }
}
  /**
   * Internal method to update the latestAssessmentAttempts collection.
   * This logic ensures previous attempts are stored correctly.
   */
  async updateExistingAttempt(latestAttemptId: string, newAttemptData: any): Promise<void> {
    const startedAt = Timestamp.now();
    const latestAttemptDocRef = doc(this.firestore, `schools/${this.tenant.id}/latestAssessmentAttempts/${latestAttemptId}`);
    // To archive the old attempt, we need to get its data first.
    const latestAttemptDocSnapshot = await getDoc(latestAttemptDocRef);
    if (!latestAttemptDocSnapshot.exists()) {
      throw new Error('Latest attempt document does not exist!');
    }
    
    const latestAttemptData = latestAttemptDocSnapshot.data() as LatestAssessmentAttempt;

    const { previousAttempts, ...latestWithoutHistory } = latestAttemptData;

    // The new attempt is a cleaned-up version of the current document.
    const newAttempt = {
      ...latestWithoutHistory,
      ...latestAttemptData,
      ...newAttemptData,
      startedAt: startedAt,
      submittedAt: null, 
      timeSpent: 0,
      score: 0,
      passed: null,
      status: 'started',
      answers: [],
      gradedAt: null,
      isManuallyGraded: false,
      manualScore: null,
      gradedBy: null,
      gradingNotes: ''
    };

    // Now, perform a single update to the existing document.
    await updateDoc(latestAttemptDocRef, {
      ...newAttempt,
      // Use arrayUnion to safely append the old attempt to the previousAttempts array.
      previousAttempts: arrayUnion(latestWithoutHistory)
    });
  }
    /**
   * Updates a specific answer within an ongoing assessment attempt.
   * This is called as the user answers each question.
   * @param attemptId The ID of the current assessment attempt.
   * @param newAnswer The AssessmentAnswer object for the current question.
   */
    async updateAttemptAnswer(attemptId: string, newAnswer: AssessmentAnswer): Promise<void> {
      const attemptDocRef = doc(this.firestore, `schools/${this.tenant.id}/latestAssessmentAttempts/${attemptId}`);

      try {
        // Fetch the current attempt to get its existing answers
        const attemptSnapshot = await getDoc(attemptDocRef);
        if (!attemptSnapshot.exists()) {
          return;
        }

        const currentAttemptData = attemptSnapshot.data() as AssessmentAttempt;
        let updatedAnswers: AssessmentAnswer[] = currentAttemptData.answers || [];

        // Find if an answer for this question already exists based on assessmentQuestionId
        const existingAnswerIndex = updatedAnswers.findIndex(ans => ans.assessmentQuestionId === newAnswer.assessmentQuestionId);

        if (existingAnswerIndex > -1) {
          // Update existing answer
          updatedAnswers[existingAnswerIndex] = { ...newAnswer, answeredAt: Timestamp.now() };
        } else {
          // Add new answer
          updatedAnswers.push({ ...newAnswer, answeredAt: Timestamp.now() });
        }

        // Update only the 'answers' field in the document
        await setDoc(attemptDocRef, { answers: updatedAnswers }, { merge: true });
      } catch (error) {
        this.messagesService.error('Something went wrong while updating the attempt answer.');
        throw error;
      }
    }

// This method should be in a separate service, e.g., AssessmentReviewService
async getQuestionsForReview(
  tenantId: string,
  latestAttempt: LatestAssessmentAttempt
): Promise<AssessmentQuestion[]> {
  const questionIds = latestAttempt.answers.map(
    (answer) => answer.assessmentQuestionId
  );

  if (questionIds.length === 0) {
    return [];
  }

  const BATCH_SIZE = 10;
  const questionIdBatches: string[][] = [];

  for (let i = 0; i < questionIds.length; i += BATCH_SIZE) {
    questionIdBatches.push(questionIds.slice(i, i + BATCH_SIZE));
  }

  const questionsMap = new Map<string, AssessmentQuestion>();

  for (const batch of questionIdBatches) {
    const batchQuerySnapshot = await getDocs(
      query(
        collection(this.firestore, `schools/${tenantId}/assessmentQuestions`),
        // Do NOT use a where('isDeleted', '==', false),filter here.
        // This is crucial for historical review.
        where("id", "in", batch)
      )
    );
    batchQuerySnapshot.forEach((doc) => {
      questionsMap.set(doc.id, {
        id: doc.id,
        ...doc.data(),
      } as AssessmentQuestion);
    });
  }

  // Now, re-order the questions based on the original attempt order
  const orderedQuestions = questionIds
    .map((id) => questionsMap.get(id))
    .filter((q) => !!q);

  return orderedQuestions as AssessmentQuestion[];
}
}
